import React, { useCallback, useMemo, useState } from 'react';
import moment from 'moment';
import { toast } from 'react-toastify';

import { unsecuredCopyToClipboard } from 'utils/functions';
import { GetConsumptionDetailsData, InstallmentsData } from '../types';
import { getBankslipForDownload } from '../services';

import {
  Container,
  DetailsWrap,
  TitleAndValueWrap,
  Title,
  Value,
  OptionsWrap,
  CopyCodeWrap,
  CopyCodeText,
  ControlsWrap,
  IconCopy,
  IconDots,
  Menu,
  MenuItem,
  StatusWrap,
  ValueStatus,
  Status,
} from './styles';

type ConsumptionHistoryDetailsProps = {
  data: GetConsumptionDetailsData[];
};

type BankSlipsFormatedData = {
  invoice: GetConsumptionDetailsData;
  installment: InstallmentsData;
};

const ConsumptionHistoryDetails: React.FC<ConsumptionHistoryDetailsProps> = ({
  data,
}) => {
  const [showMenu, setShowMenu] = useState<number | undefined>();

  const handleCopyCod = useCallback(
    ({ installment }: BankSlipsFormatedData) => {
      if (window.isSecureContext && navigator.clipboard) {
        navigator.clipboard.writeText(installment.typeableLine);
      } else {
        unsecuredCopyToClipboard(installment.typeableLine);
      }
      toast.success(`Código de barras copiado com sucesso!`);
    },
    []
  );

  const handleForEmail = useCallback(
    (bankSlipsParam: BankSlipsFormatedData): void => {
      const { invoice, installment } = bankSlipsParam;
      getBankslipForDownload({ invoice, installment, deliveryMethod: 'EMAIL' });
    },
    []
  );

  const handleForPdfFile = useCallback(
    (bankSlipsParam: BankSlipsFormatedData): void => {
      getBankslipForDownload(bankSlipsParam);
    },
    []
  );

  const menuItems = useMemo(() => {
    return [
      {
        value: 'Enviar por email',
        action: handleForEmail,
      },
      {
        value: 'Baixar em PDF',
        action: handleForPdfFile,
      },
      {
        value: 'Copiar Código de barras',
        action: handleCopyCod,
      },
    ];
  }, [handleCopyCod, handleForEmail, handleForPdfFile]);

  const getStatusColor = (status: string): string => {
    const statusFormatted = (status || '')
      .replace('_', ' ')
      .toLocaleLowerCase();
    switch (statusFormatted) {
      case 'pago':
        return '#75B943';
      case 'vencido':
        return '#e34e2a';
      case 'a vencer':
        return '#f0b01b';
      default:
        return '#000000';
    }
  };

  const bankSlips: BankSlipsFormatedData[] = useMemo(() => {
    let bankSlipsFormated: BankSlipsFormatedData[] = [];
    data.forEach((invoice) => {
      invoice.installments.forEach((installment) => {
        let dateFormated = installment.dueDate;

        dateFormated = moment(installment.dueDate).format('DD/MM/YYYY');

        if (dateFormated === 'Invalid date') {
          dateFormated = installment.dueDate;
        }
        const statusFormatted = installment.status.toLocaleLowerCase();
        const valueFormatted = installment.amountOriginal.toLocaleString(
          'pt-br',
          { style: 'currency', currency: 'BRL' }
        );
        installment.dueDate = dateFormated;
        installment.valueFormatted = valueFormatted;
        installment.status = statusFormatted;
        bankSlipsFormated = [...bankSlipsFormated, { invoice, installment }];
      });
    });
    return bankSlipsFormated;
  }, [data]);

  return (
    <Container>
      {bankSlips.map((bankSlip, idx) => {
        const { installment, invoice } = bankSlip;

        return (
          <DetailsWrap>
            <TitleAndValueWrap gridArea="vencimento">
              <Title>Vencimento</Title>
              <Value>{installment.dueDate}</Value>
            </TitleAndValueWrap>
            <TitleAndValueWrap gridArea="valor">
              <Title>Valor (R$)</Title>
              <Value>{installment.valueFormatted}</Value>
            </TitleAndValueWrap>
            <TitleAndValueWrap gridArea="volume">
              <Title>Volume(kg)</Title>
              <Value>{invoice.consumption}</Value>
            </TitleAndValueWrap>
            <TitleAndValueWrap gridArea="situacao">
              <Title>Situação</Title>
              <StatusWrap>
                <Status color={getStatusColor(installment.status)} />
                <ValueStatus>{installment.status}</ValueStatus>
              </StatusWrap>
            </TitleAndValueWrap>
            <OptionsWrap gridArea="opcoes">
              <CopyCodeWrap onClick={() => handleCopyCod(bankSlip)}>
                <IconCopy size={22} color="#0B29FB" />
                <CopyCodeText>Copiar código</CopyCodeText>
              </CopyCodeWrap>
              <ControlsWrap
                onClick={() => setShowMenu(idx === showMenu ? undefined : idx)}
              >
                <IconDots size={22} color="#0B29FB" />
                {showMenu === idx && (
                  <Menu>
                    {menuItems.map((menuItem) => (
                      <MenuItem onClick={() => menuItem.action(bankSlip)}>
                        {menuItem.value}
                      </MenuItem>
                    ))}
                  </Menu>
                )}
              </ControlsWrap>
            </OptionsWrap>
          </DetailsWrap>
        );
      })}
    </Container>
  );
};

export default ConsumptionHistoryDetails;
